import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, FilterIcon, TrendingUpIcon, AlertCircleIcon, CheckCircle2Icon, UsersIcon, BuildingIcon, SparklesIcon, LayoutGridIcon, ListIcon, MapPinIcon, XIcon } from 'lucide-react';
import { AccountCard } from '../components/AccountCard';
import { seedData } from '../data/seedData';
import { useUser } from '../lib/userContext';
import { MiniSparkline } from '../components/MiniSparkline';
import { LocationMap } from '../components/LocationMap';
interface HomeProps {
  onOpenPartnerView: (accountId: string) => void;
}
interface AccountLocation {
  name: string;
  type: 'client' | 'quad_service_center';
  address: string;
  description: string;
}
export function Home({
  onOpenPartnerView
}: HomeProps) {
  const {
    currentUser,
    hasPermission
  } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [healthFilter, setHealthFilter] = useState<'All' | 'Green' | 'Yellow' | 'Red'>('All');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedAccountForMap, setSelectedAccountForMap] = useState<string | null>(null);
  // Calculate stats from ALL accounts (not filtered)
  const allAccountsStats = useMemo(() => {
    const total = seedData.accounts.length;
    const green = seedData.accounts.filter(a => a.health === 'Green').length;
    const yellow = seedData.accounts.filter(a => a.health === 'Yellow').length;
    const red = seedData.accounts.filter(a => a.health === 'Red').length;
    const totalJobs = seedData.accounts.reduce((sum, a) => sum + a.kpis.active_jobs, 0);
    const totalAlerts = seedData.accounts.reduce((sum, a) => sum + a.kpis.sla_alerts, 0);
    return {
      total,
      green,
      yellow,
      red,
      totalJobs,
      totalAlerts
    };
  }, []);
  // Filter accounts based on permissions and filters
  const filteredAccounts = useMemo(() => {
    let accounts = seedData.accounts;
    // Apply role-based filtering
    if (currentUser.role === 'Account Admin' || currentUser.role === 'Account Member') {
      accounts = accounts.filter(acc => acc.primary_owner.id === currentUser.id || acc.quad_team?.some(p => p.id === currentUser.id));
    }
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      accounts = accounts.filter(acc => acc.name.toLowerCase().includes(query) || acc.industry.toLowerCase().includes(query) || acc.region.toLowerCase().includes(query) || acc.products_services.some(p => p.toLowerCase().includes(query)));
    }
    // Apply health filter
    if (healthFilter !== 'All') {
      accounts = accounts.filter(acc => acc.health === healthFilter);
    }
    return accounts;
  }, [currentUser, searchQuery, healthFilter]);
  // Generate trend data for sparklines
  const generateTrendData = (baseValue: number) => {
    return Array.from({
      length: 7
    }, () => Math.max(0, baseValue + Math.floor(Math.random() * 10 - 5)));
  };
  // Enhanced location data generation using real account data
  const getAccountLocations = (accountId: string): AccountLocation[] => {
    const account = seedData.accounts.find(a => a.id === accountId);
    if (!account) return [];
    const locations: AccountLocation[] = [];
    // Extract unique client locations from people data
    const clientLocations = new Set<string>();
    account.people?.filter(p => p.organization === 'Client' && p.location).forEach(p => {
      if (p.location) clientLocations.add(p.location);
    });
    // Add client locations
    Array.from(clientLocations).forEach((location, index) => {
      const isHQ = index === 0;
      locations.push({
        name: isHQ ? `${account.name} Headquarters` : `${account.name} ${location} Office`,
        type: 'client',
        address: location,
        description: isHQ ? `Primary headquarters and decision-making center for ${account.name}` : `Regional office serving ${location} area operations`
      });
    });
    // Extract Quad service center locations from Quad team members
    const quadLocations = new Set<string>();
    account.people?.filter(p => p.organization === 'Quad' && p.location).forEach(p => {
      if (p.location) quadLocations.add(p.location);
    });
    // Add Quad service centers based on team locations
    Array.from(quadLocations).forEach(location => {
      // Determine facility type based on industry
      let facilityType = 'Production & Distribution Center';
      let description = `Full-service production facility serving ${account.name}`;
      if (account.industry === 'Retail') {
        facilityType = 'Retail Marketing Production Center';
        description = `Specialized retail marketing and in-store signage production facility`;
      } else if (account.industry === 'Pharmacy') {
        facilityType = 'Healthcare Communications Center';
        description = `Compliant healthcare and pharmacy communications production facility`;
      } else if (account.industry === 'Grocery') {
        facilityType = 'Grocery Marketing Production Center';
        description = `High-volume circular and promotional materials production facility`;
      } else if (account.industry === 'Technology') {
        facilityType = 'Digital & Print Innovation Center';
        description = `Advanced digital and print production facility with technical capabilities`;
      }
      locations.push({
        name: `Quad ${location} ${facilityType}`,
        type: 'quad_service_center',
        address: location,
        description
      });
    });
    // If no locations found, add at least a headquarters based on region
    if (locations.length === 0) {
      const defaultLocation = account.region === 'North America' ? 'New York, NY' : account.region === 'Europe' ? 'London, UK' : account.region === 'Asia Pacific' ? 'Singapore' : 'New York, NY';
      locations.push({
        name: `${account.name} Headquarters`,
        type: 'client',
        address: defaultLocation,
        description: `Primary headquarters for ${account.name}`
      });
      locations.push({
        name: 'Quad Chicago Production Center',
        type: 'quad_service_center',
        address: 'Chicago, IL',
        description: `Primary production facility serving ${account.name}`
      });
    }
    return locations;
  };
  const selectedAccountLocations = selectedAccountForMap ? getAccountLocations(selectedAccountForMap) : [];
  return <div className="space-y-8" data-id="element-1859">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden" data-id="element-1860">
        <div className="absolute inset-0 -z-10" data-id="element-1861">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" data-id="element-1862" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" data-id="element-1863" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative" data-id="element-1864">
          <div className="flex items-center justify-between mb-6" data-id="element-1865">
            <div data-id="element-1866">
              <h1 className="text-4xl font-bold text-white mb-2 tracking-tight bg-gradient-to-r from-white via-blue-50 to-slate-200 bg-clip-text text-transparent" data-id="element-1867">
                Client Accounts
              </h1>
              <p className="text-slate-400 text-lg" data-id="element-1868">
                Manage and monitor your client portfolio
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-1869">
              <button onClick={() => setViewMode('grid')} className={`p-3 rounded-xl transition-all duration-200 ${viewMode === 'grid' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/40' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50'}`} title="Grid View" data-id="element-1870">
                <LayoutGridIcon className="w-5 h-5" data-id="element-1871" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-3 rounded-xl transition-all duration-200 ${viewMode === 'list' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/40' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50'}`} title="List View" data-id="element-1872">
                <ListIcon className="w-5 h-5" data-id="element-1873" />
              </button>
            </div>
          </div>
          {/* Enhanced Stats Grid - Now shows ALL accounts stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-id="element-1874">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1875">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1876" />
              <div className="relative" data-id="element-1877">
                <div className="flex items-center justify-between mb-3" data-id="element-1878">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors" data-id="element-1879">
                    <BuildingIcon className="w-5 h-5 text-blue-400" data-id="element-1880" />
                  </div>
                  <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-1881">
                    {allAccountsStats.total}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-1882">
                  Total Accounts
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1883">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1884" />
              <div className="relative" data-id="element-1885">
                <div className="flex items-center justify-between mb-3" data-id="element-1886">
                  <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors" data-id="element-1887">
                    <CheckCircle2Icon className="w-5 h-5 text-green-400" data-id="element-1888" />
                  </div>
                  <span className="text-3xl font-bold text-green-400 group-hover:scale-110 transition-transform" data-id="element-1889">
                    {allAccountsStats.green}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-1890">
                  Green Health
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1891">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 via-transparent to-yellow-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1892" />
              <div className="relative" data-id="element-1893">
                <div className="flex items-center justify-between mb-3" data-id="element-1894">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center group-hover:bg-yellow-500/20 transition-colors" data-id="element-1895">
                    <AlertCircleIcon className="w-5 h-5 text-yellow-400" data-id="element-1896" />
                  </div>
                  <span className="text-3xl font-bold text-yellow-400 group-hover:scale-110 transition-transform" data-id="element-1897">
                    {allAccountsStats.yellow}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-1898">
                  Yellow Health
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1899">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 via-transparent to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1900" />
              <div className="relative" data-id="element-1901">
                <div className="flex items-center justify-between mb-3" data-id="element-1902">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors" data-id="element-1903">
                    <AlertCircleIcon className="w-5 h-5 text-red-400" data-id="element-1904" />
                  </div>
                  <span className="text-3xl font-bold text-red-400 group-hover:scale-110 transition-transform" data-id="element-1905">
                    {allAccountsStats.red}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-1906">
                  Red Health
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1907">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1908" />
              <div className="relative" data-id="element-1909">
                <div className="flex items-center justify-between mb-3" data-id="element-1910">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors" data-id="element-1911">
                    <TrendingUpIcon className="w-5 h-5 text-purple-400" data-id="element-1912" />
                  </div>
                  <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-1913">
                    {allAccountsStats.totalJobs}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-1914">
                  Active Jobs
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1915">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1916" />
              <div className="relative" data-id="element-1917">
                <div className="flex items-center justify-between mb-3" data-id="element-1918">
                  <div className={`w-10 h-10 rounded-xl ${allAccountsStats.totalAlerts > 0 ? 'bg-red-500/10' : 'bg-green-500/10'} flex items-center justify-center group-hover:bg-red-500/20 transition-colors`} data-id="element-1919">
                    <AlertCircleIcon className={`w-5 h-5 ${allAccountsStats.totalAlerts > 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-1920" />
                  </div>
                  <span className={`text-3xl font-bold group-hover:scale-110 transition-transform ${allAccountsStats.totalAlerts > 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-1921">
                    {allAccountsStats.totalAlerts}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-1922">
                  SLA Alerts
                </p>
              </div>
            </motion.div>
          </div>
          {/* Enhanced Filters */}
          <div className="flex flex-col md:flex-row gap-4 pb-2" data-id="element-1923">
            {/* Search */}
            <div className="flex-1 relative" data-id="element-1924">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-1925" />
              <input type="text" placeholder="Search accounts by name, industry, or products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-slate-900/60 border border-slate-800/80 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all duration-200 shadow-lg shadow-black/20" data-id="element-1926" />
            </div>
            {/* Health Filter */}
            <div className="flex gap-2 pb-1" data-id="element-1927">
              {(['All', 'Green', 'Yellow', 'Red'] as const).map(filter => <button key={filter} onClick={() => setHealthFilter(filter)} className={`px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${healthFilter === filter ? filter === 'All' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white ring-1 ring-blue-500/30 scale-[1.01]' : filter === 'Green' ? 'bg-gradient-to-r from-green-600 to-green-700 text-white ring-1 ring-green-500/30 scale-[1.01]' : filter === 'Yellow' ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white ring-1 ring-yellow-500/30 scale-[1.01]' : 'bg-gradient-to-r from-red-600 to-red-700 text-white ring-1 ring-red-500/30 scale-[1.01]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'}`} data-id="element-1928">
                  {filter}
                </button>)}
            </div>
          </div>
        </motion.div>
      </div>
      {/* Accounts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1929">
        {filteredAccounts.map(account => <AccountCard key={account.id} account={account} onOpenPartnerView={botType => {
        console.log('[Home] onOpenPartnerView called from AccountCard');
        console.log('[Home] Received botType:', botType);
        console.log('[Home] Account context:', {
          id: account.id,
          name: account.name
        });
        console.log('[Home] Calling parent onOpenPartnerView with:', {
          accountId: account.id,
          botType: botType
        });
        onOpenPartnerView(account.id, botType);
      }} onShowLocationIntel={() => setSelectedAccountForMap(account.id)} data-id="element-1930" />)}
      </div>
      {/* Location Intelligence Modal */}
      <AnimatePresence data-id="element-1931">
        {selectedAccountForMap && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setSelectedAccountForMap(null)} data-id="element-1932">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} onClick={e => e.stopPropagation()} className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800/80 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" data-id="element-1933">
              {/* Header */}
              <div className="relative px-6 py-5 border-b border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950" data-id="element-1934">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-1935" />
                <div className="relative flex items-center justify-between" data-id="element-1936">
                  <div className="flex items-center gap-4" data-id="element-1937">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg" data-id="element-1938">
                      <MapPinIcon className="w-6 h-6 text-white" data-id="element-1939" />
                    </div>
                    <div data-id="element-1940">
                      <h3 className="text-xl font-bold text-white mb-1" data-id="element-1941">
                        Location & Logistics Intelligence
                      </h3>
                      <p className="text-sm text-slate-400" data-id="element-1942">
                        {seedData.accounts.find(a => a.id === selectedAccountForMap)?.name}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedAccountForMap(null)} className="p-2.5 rounded-xl bg-slate-800/60 hover:bg-slate-700 text-slate-400 hover:text-white transition-all hover:scale-105" data-id="element-1943">
                    <XIcon className="w-5 h-5" data-id="element-1944" />
                  </button>
                </div>
              </div>
              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-1945">
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-1946">
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 rounded-xl p-4" data-id="element-1947">
                    <div className="text-xs text-blue-400 mb-2 font-semibold uppercase tracking-wider" data-id="element-1948">
                      Client Sites
                    </div>
                    <div className="text-2xl font-bold text-white" data-id="element-1949">
                      {selectedAccountLocations.filter(l => l.type === 'client').length}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/20 rounded-xl p-4" data-id="element-1950">
                    <div className="text-xs text-purple-400 mb-2 font-semibold uppercase tracking-wider" data-id="element-1951">
                      Service Centers
                    </div>
                    <div className="text-2xl font-bold text-white" data-id="element-1952">
                      {selectedAccountLocations.filter(l => l.type === 'quad_service_center').length}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 rounded-xl p-4" data-id="element-1953">
                    <div className="text-xs text-green-400 mb-2 font-semibold uppercase tracking-wider" data-id="element-1954">
                      Avg. Transit
                    </div>
                    <div className="text-2xl font-bold text-white" data-id="element-1955">
                      2.3 days
                    </div>
                  </div>
                </div>
                {/* Two Column Layout: Map + Logistics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-1956">
                  {/* Compact Map */}
                  <div data-id="element-1957">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1958">
                      Geographic Coverage
                    </h4>
                    <LocationMap locations={selectedAccountLocations} data-id="element-1959" />
                  </div>
                  {/* Shipping & Logistics Intelligence */}
                  <div className="space-y-4" data-id="element-1960">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1961">
                      Shipping Intelligence
                    </h4>
                    {/* Delivery Performance */}
                    <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 rounded-xl p-4" data-id="element-1962">
                      <div className="flex items-center justify-between mb-3" data-id="element-1963">
                        <h5 className="text-sm font-bold text-white" data-id="element-1964">
                          On-Time Delivery
                        </h5>
                        <span className="text-2xl font-bold text-green-400" data-id="element-1965">
                          98.5%
                        </span>
                      </div>
                      <div className="w-full bg-slate-800/60 rounded-full h-2 mb-2" data-id="element-1966">
                        <div className="bg-gradient-to-r from-green-500 to-green-400 h-2 rounded-full" style={{
                      width: '98.5%'
                    }} data-id="element-1967" />
                      </div>
                      <p className="text-xs text-slate-400" data-id="element-1968">
                        Last 90 days performance
                      </p>
                    </div>
                    {/* Transit Times */}
                    <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 rounded-xl p-4" data-id="element-1969">
                      <h5 className="text-sm font-bold text-white mb-3" data-id="element-1970">
                        Average Transit Times
                      </h5>
                      <div className="space-y-2" data-id="element-1971">
                        <div className="flex items-center justify-between text-sm" data-id="element-1972">
                          <span className="text-slate-400" data-id="element-1973">
                            Standard Shipping
                          </span>
                          <span className="text-white font-semibold" data-id="element-1974">
                            2-3 days
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm" data-id="element-1975">
                          <span className="text-slate-400" data-id="element-1976">
                            Express Shipping
                          </span>
                          <span className="text-white font-semibold" data-id="element-1977">
                            1-2 days
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm" data-id="element-1978">
                          <span className="text-slate-400" data-id="element-1979">Rush Orders</span>
                          <span className="text-white font-semibold" data-id="element-1980">
                            24-48 hrs
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Shipping Routes */}
                    <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/20 rounded-xl p-4" data-id="element-1981">
                      <h5 className="text-sm font-bold text-white mb-3" data-id="element-1982">
                        Active Shipping Routes
                      </h5>
                      <div className="space-y-2" data-id="element-1983">
                        {selectedAccountLocations.filter(l => l.type === 'quad_service_center').slice(0, 3).map((location, index) => <div key={index} className="flex items-center gap-2 text-sm" data-id="element-1984">
                              <div className="w-2 h-2 rounded-full bg-purple-400" data-id="element-1985" />
                              <span className="text-slate-300" data-id="element-1986">
                                {location.address} → Client Sites
                              </span>
                            </div>)}
                      </div>
                    </div>
                    {/* Cost Optimization */}
                    <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/20 rounded-xl p-4" data-id="element-1987">
                      <h5 className="text-sm font-bold text-white mb-3" data-id="element-1988">
                        Cost Efficiency
                      </h5>
                      <div className="space-y-2" data-id="element-1989">
                        <div className="flex items-center justify-between text-sm" data-id="element-1990">
                          <span className="text-slate-400" data-id="element-1991">
                            Avg. Cost per Shipment
                          </span>
                          <span className="text-white font-semibold" data-id="element-1992">$127</span>
                        </div>
                        <div className="flex items-center justify-between text-sm" data-id="element-1993">
                          <span className="text-slate-400" data-id="element-1994">
                            Volume Discount
                          </span>
                          <span className="text-green-400 font-semibold" data-id="element-1995">
                            -18%
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm" data-id="element-1996">
                          <span className="text-slate-400" data-id="element-1997">
                            Monthly Savings
                          </span>
                          <span className="text-green-400 font-semibold" data-id="element-1998">
                            $4,200
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Locations List - More Compact */}
                <div className="space-y-3" data-id="element-1999">
                  <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2000">
                    All Locations ({selectedAccountLocations.length})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2001">
                    {selectedAccountLocations.map((location, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.05,
                  type: 'spring',
                  damping: 20,
                  stiffness: 300
                }} className={`group relative p-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] ${location.type === 'client' ? 'bg-gradient-to-br from-blue-500/5 to-blue-600/5 border-blue-500/20 hover:border-blue-500/40' : 'bg-gradient-to-br from-purple-500/5 to-purple-600/5 border-purple-500/20 hover:border-purple-500/40'}`} data-id="element-2002">
                        <div className="flex items-start gap-3" data-id="element-2003">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${location.type === 'client' ? 'bg-blue-500/10 group-hover:bg-blue-500/20' : 'bg-purple-500/10 group-hover:bg-purple-500/20'}`} data-id="element-2004">
                            {location.type === 'client' ? <BuildingIcon className="w-5 h-5 text-blue-400" data-id="element-2005" /> : <MapPinIcon className="w-5 h-5 text-purple-400" data-id="element-2006" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2007">
                            <h4 className="text-sm font-semibold text-white mb-1 truncate" data-id="element-2008">
                              {location.name}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-slate-400 mb-1" data-id="element-2009">
                              <MapPinIcon className="w-3 h-3" data-id="element-2010" />
                              <span className="truncate" data-id="element-2011">
                                {location.address}
                              </span>
                            </div>
                            {location.type === 'quad_service_center' && <div className="flex items-center gap-1.5 mt-2" data-id="element-2012">
                                <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded text-xs font-semibold" data-id="element-2013">
                                  Active
                                </span>
                                <span className="px-2 py-0.5 bg-slate-800/60 text-slate-400 rounded text-xs" data-id="element-2014">
                                  50mi radius
                                </span>
                              </div>}
                          </div>
                        </div>
                      </motion.div>)}
                  </div>
                </div>
                {/* Service Coverage Insights */}
                <div className="mt-6 p-5 bg-gradient-to-br from-slate-800/40 to-slate-900/40 border border-slate-700/40 rounded-xl" data-id="element-2015">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2" data-id="element-2016">
                    <SparklesIcon className="w-4 h-4 text-blue-400" data-id="element-2017" />
                    Logistics Insights
                  </h4>
                  <div className="space-y-2 text-sm text-slate-300" data-id="element-2018">
                    <p className="flex items-start gap-2" data-id="element-2019">
                      <CheckCircle2Icon className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" data-id="element-2020" />
                      <span data-id="element-2021">
                        All client locations within optimal service radius
                      </span>
                    </p>
                    <p className="flex items-start gap-2" data-id="element-2022">
                      <CheckCircle2Icon className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" data-id="element-2023" />
                      <span data-id="element-2024">
                        Multiple distribution centers provide delivery
                        redundancy
                      </span>
                    </p>
                    <p className="flex items-start gap-2" data-id="element-2025">
                      <TrendingUpIcon className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-2026" />
                      <span data-id="element-2027">
                        98.5% on-time delivery rate exceeds industry standard
                      </span>
                    </p>
                    <p className="flex items-start gap-2" data-id="element-2028">
                      <TrendingUpIcon className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" data-id="element-2029" />
                      <span data-id="element-2030">
                        Volume discounts saving $4,200/month on shipping costs
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}